/*
 * Woerdman — Cadeau-kiezer (choose your free gift)
 * -------------------------------------------------------------
 * Toont een keuze uit meerdere cadeaus zodra het mandje een drempelbedrag aan
 * een collectie bevat (bv. €100 aan HKLiving). De klant kiest er één -> die
 * wordt gratis toegevoegd (de €0 regelt een aparte Shopify BXGY-korting).
 * Onder de drempel -> gekozen cadeau weer weg. Wisselen mag (andere mok kiezen).
 *
 * Config via data-attributen op het <gift-chooser>-element (zie
 * snippets/woerdman-gift-chooser.liquid). Deze code regelt alleen tonen/kiezen/
 * verwijderen — niet de prijs.
 */
if (!customElements.get('gift-chooser')) {
  customElements.define(
    'gift-chooser',
    class GiftChooser extends HTMLElement {
      connectedCallback() {
        this.enabled = this.dataset.enabled === 'true';
        this.collectionHandle = this.dataset.collectionHandle;
        this.minAmount = parseInt(this.dataset.minAmount, 10) || 0; // in centen
        this.message = this.dataset.message || 'Gratis cadeau';
        this.giftProductIds = (this.dataset.giftProductIds || '')
          .split(',')
          .map((s) => parseInt(s, 10))
          .filter(Boolean);

        this.busy = false;
        this.eligibleIds = null;

        if (!this.enabled || !this.collectionHandle || !this.minAmount || !this.giftProductIds.length) return;
        if (!window.FoxTheme || !FoxTheme.pubsub) return;

        this.onClickHandler = this.onClick.bind(this);
        this.addEventListener('click', this.onClickHandler);

        this.debounced = FoxTheme.utils.debounce(this.reconcileFromServer.bind(this), 300);
        this.unsub = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          if (!(window.Shopify && Shopify.designMode)) this.debounced();
        });

        if (!(window.Shopify && Shopify.designMode)) this.reconcileFromServer();
      }

      disconnectedCallback() {
        if (this.unsub) this.unsub();
        this.removeEventListener('click', this.onClickHandler);
      }

      /* Product-id's van de trigger-collectie ophalen en cachen. */
      async loadEligibleIds() {
        if (this.eligibleIds) return this.eligibleIds;
        const cacheKey = 'wgiftchooser:' + this.collectionHandle;
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            this.eligibleIds = new Set(JSON.parse(cached));
            return this.eligibleIds;
          }
        } catch (e) {}
        const ids = new Set();
        let page = 1;
        while (page <= 10) {
          let res;
          try {
            res = await fetch(`/collections/${this.collectionHandle}/products.json?limit=250&page=${page}`);
          } catch (e) {
            break;
          }
          if (!res.ok) break;
          const data = await res.json();
          const products = (data && data.products) || [];
          products.forEach((p) => ids.add(p.id));
          if (products.length < 250) break;
          page++;
        }
        this.eligibleIds = ids;
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify([...ids]));
        } catch (e) {}
        return ids;
      }

      async getCart() {
        return (await fetch(`${FoxTheme.routes.cart_url}.js`)).json();
      }

      /* Som (in centen) van de trigger-collectie in het mandje, exclusief de cadeaus. */
      collectionSubtotal(cart) {
        if (!this.eligibleIds) return 0;
        return cart.items.reduce((sum, i) => {
          if (this.giftProductIds.indexOf(i.product_id) === -1 && this.eligibleIds.has(i.product_id)) {
            return sum + i.original_line_price;
          }
          return sum;
        }, 0);
      }

      /* De gekozen cadeau-regel (een van de mokken met _gift-kenmerk). */
      giftLine(cart) {
        return cart.items.find(
          (i) => this.giftProductIds.indexOf(i.product_id) !== -1 && i.properties && i.properties._gift
        );
      }

      async reconcileFromServer() {
        if (this.busy) return;
        try {
          await this.reconcile(await this.getCart());
        } catch (e) {}
      }

      async reconcile(cart) {
        if (this.busy || !cart || !cart.items) return;
        await this.loadEligibleIds();

        const subtotal = this.collectionSubtotal(cart);
        const gl = this.giftLine(cart);
        const qualifies = subtotal >= this.minAmount;

        // Onder de drempel: gekozen cadeau weg + kiezer verbergen.
        if (!qualifies) {
          if (gl) {
            this.busy = true;
            try {
              await this.change(gl.key, 0);
            } catch (e) {} finally {
              this.busy = false;
            }
            await this.refreshCart();
            return;
          }
          this.setAttribute('hidden', '');
          return;
        }

        // Boven de drempel: kiezer tonen, gekozen mok markeren.
        this.removeAttribute('hidden');
        const chosen = gl ? String(gl.variant_id) : null;
        this.querySelectorAll('[data-gift-variant]').forEach((btn) => {
          const isChosen = String(btn.dataset.giftVariant) === chosen;
          btn.classList.toggle('is-chosen', isChosen);
          btn.setAttribute('aria-pressed', isChosen ? 'true' : 'false');
        });
        this.classList.toggle('has-choice', !!gl);
      }

      async onClick(e) {
        const btn = e.target.closest('[data-gift-variant]');
        if (!btn || this.busy) return;
        e.preventDefault();
        const variantId = parseInt(btn.dataset.giftVariant, 10);
        if (!variantId) return;

        const cart = await this.getCart();
        const gl = this.giftLine(cart);
        if (gl && gl.variant_id === variantId) return; // deze is al gekozen

        this.busy = true;
        try {
          if (gl) await this.change(gl.key, 0); // oude keuze weg
          await fetch(`${FoxTheme.routes.cart_add_url}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _gift: this.message } }] }),
          });
        } catch (e) {} finally {
          this.busy = false;
        }
        await this.refreshCart();
      }

      async change(key, qty) {
        await fetch(`${FoxTheme.routes.cart_url}/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
        });
      }

      /* Ververst zoals het thema zelf doet (lade + winkelwagenpagina). */
      async refreshCart() {
        try {
          const sectionIds = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionIds } })
          );
          const ids = [...new Set(sectionIds)].filter(Boolean);
          const cart = await this.getCart();
          if (ids.length) {
            const base = FoxTheme.routes.root_url && FoxTheme.routes.root_url.length ? FoxTheme.routes.root_url : '/';
            const sep = base.indexOf('?') > -1 ? '&' : '?';
            const res = await fetch(`${base}${sep}sections=${ids.join(',')}`);
            if (res.ok) cart.sections = await res.json();
          }
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        } catch (e) {}
      }
    }
  );
}
